import React from 'react';
import { Grid, GridItem, useBreakpointValue } from '@chakra-ui/react';
import { AnimatePresence } from 'framer-motion';
import ConversationList from './_components/ConversationList';
import ConversationView from './_components/ConversationView';
import ContactInfoPanel from './_components/ContactInfoPanel';
import { MotionBox, useMotionPrefs, EASE } from './_components/motion';
import useMetaWhatsappSocket from './hooks/useMetaWhatsappSocket';

/**
 * The WhatsApp inbox.
 *
 * Three panes, the way WhatsApp Web lays them out: the threads a user may see
 * on the left, the open one in the middle, and — when asked for — the contact
 * panel on the right. On a phone-sized screen the list slides away for the
 * thread and back again, rather than both squeezing into an unusable width.
 *
 * ★ THIS SCREEN IS INVISIBLE WITHOUT PERMISSION.
 * The route declares `parent` and `childId`, so the sidebar filter hides it
 * unless the Meta actions of the `whatsapp` module are granted — and
 * permissions ship denied, so no existing user sees new navigation appear. The
 * server refuses regardless; the navigation filter is convenience, never the
 * control.
 */
export default function MetaWhatsappInbox() {
	const [selectedId, setSelectedId] = React.useState(null);
	const [infoOpen, setInfoOpen] = React.useState(false);
	const { dur } = useMotionPrefs();

	// Live updates. The subscribe carries no room name — the server resolves
	// which channels this user may see and joins exactly those.
	useMetaWhatsappSocket();

	// The second argument is the breakpoint assumed before the first
	// measurement, so a desktop does not flash the phone layout on load.
	const isMobile = useBreakpointValue({ base: true, md: false }, 'md') ?? false;
	// Wide enough for the contact panel to be a column; below that it is a
	// drawer over the thread, still INSIDE the measured shell's height.
	const infoAsColumn = useBreakpointValue({ base: false, xl: true }, 'xl') ?? false;

	const showInfo = infoOpen && Boolean(selectedId);
	// ★ THE RAILS NARROW BEFORE THE CONVERSATION DOES.
	// A fixed 320px list is a third of a 1024px tablet, leaving the thread less
	// room than the index to it. The list only ever shows a name, a preview line
	// and a time, all of which ellipsize, so it is the pane that can afford to
	// give — and the conversation is what the screen is for.
	const railWidth = useBreakpointValue({ md: '260px', lg: '300px', xl: '320px' }, 'lg') ?? '300px';
	const columns = isMobile
		? '1fr'
		: showInfo && infoAsColumn
			? `${railWidth} minmax(0, 1fr) 320px`
			: `${railWidth} minmax(0, 1fr)`;

	const openInfo = React.useCallback(() => setInfoOpen(true), []);
	const closeInfo = React.useCallback(() => setInfoOpen(false), []);
	const back = React.useCallback(() => setSelectedId(null), []);

	const thread = (
		<ConversationView
			conversationId={selectedId}
			onBack={isMobile ? back : undefined}
			onOpenInfo={openInfo}
		/>
	);

	return (
		// ★ THIS SCREEN SIZES ITSELF. NOTHING IN THE APP SHELL KNOWS ABOUT IT.
		// The shell used to give this route its own height mode — a `100vh` flex
		// chain threaded through three boxes so this could be `height: 100%`. It
		// held on some viewports and quietly failed on others, and when it failed
		// the composer sat below a clipped edge with no way to scroll to it. The
		// WhatsApp V1 chat has never had that problem because it asks the shell
		// for nothing: one `h='80vh'` on its own root and it is done
		// (`views/admin/whatsapp/index.js`). Same thing here.
		//
		// `vh` and not `%`: a percentage needs an ancestor with a definite height,
		// which is the dependency that just cost two days. `vh` is answered by the
		// viewport alone, so this is right on first paint, at every width, under
		// any shell — and because the shell is an ordinary scrolling page again,
		// anything that ever did overflow stays reachable instead of being clipped.
		<Grid
			templateColumns={columns}
			// ★ WITHOUT THIS ROW THE COMPOSER IS UNREACHABLE.
			// A grid with no `templateRows` gets ONE IMPLICIT `auto` ROW, and an
			// `auto` row is sized by its content — so the row grew to the full
			// height of the message list (3,460px on a normal thread) while this
			// box stayed at its own 788px with `overflow: hidden`. Every child's
			// `h="100%"` then resolved against the 3,460, not against the 788:
			// the column overflowed the clipped box, the composer landed ~2,600px
			// below the fold with nothing to scroll to, and the message list —
			// already `flex: 1; min-height: 0; overflow-y: auto` — never received
			// a bounded height, so it had nothing to scroll either. Both symptoms,
			// one cause.
			//
			// `minmax(0, 1fr)` rather than `1fr`: a bare `1fr` keeps an automatic
			// minimum of min-content, which lets a tall child push the row open
			// again and reinstates exactly this bug. The 0 minimum is what makes
			// the container's height the budget.
			templateRows="minmax(0, 1fr)"
			gap="0"
			// Taller than V1's 80vh because this screen has no toolbar above it,
			// and a floor in px so the composer still has room on a short laptop
			// window, where 84% of a 500px viewport would leave nothing to type in.
			// ★ `dvh`, NOT `vh`, AND MORE OF IT ON A PHONE.
			// `vh` is the viewport with the mobile browser's own chrome ignored, so
			// on a phone it is taller than anything anyone can see and the bottom
			// of the chat — the composer — sits under the URL bar. `dvh` tracks
			// what is actually visible. The share is larger on a phone because a
			// chat IS the screen there, while on a desktop it is one panel on a
			// page that has other things on it.
			h={{ base: '88dvh', md: '84dvh' }}
			// ⚠ THE FLOOR IS PER-DEVICE TOO. A flat 520px is taller than a phone
			// held sideways (a 360px-tall landscape viewport), which forced the
			// page to scroll to reach a chat that is meant to fit on it.
			minH={{ base: '360px', md: '520px' }}
			// Edge to edge on a phone: a border and a 8px radius around something
			// occupying the whole screen is a frame around nothing.
			borderWidth={{ base: 0, md: '1px' }}
			borderColor="wa.divider"
			borderRadius={{ base: 0, md: 'lg' }}
			overflow="hidden"
			bg="wa.app"
			transition="grid-template-columns 200ms ease"
		>
			{isMobile ? (
				<GridItem h="100%" position="relative" overflow="hidden">
					<AnimatePresence initial={false}>
						{selectedId ? (
							<MotionBox
								key="thread"
								position="absolute"
								inset="0"
								initial={{ x: '100%' }}
								animate={{ x: 0 }}
								exit={{ x: '100%' }}
								transition={{ duration: dur(200), ease: EASE }}
							>
								{thread}
							</MotionBox>
						) : (
							<MotionBox
								key="list"
								position="absolute"
								inset="0"
								initial={{ x: '-30%', opacity: 0 }}
								animate={{ x: 0, opacity: 1 }}
								exit={{ x: '-30%', opacity: 0 }}
								transition={{ duration: dur(200), ease: EASE }}
							>
								<ConversationList selectedId={selectedId} onSelect={setSelectedId} />
							</MotionBox>
						)}
					</AnimatePresence>
				</GridItem>
			) : (
				<>
					<GridItem h="100%" minW="0">
						<ConversationList selectedId={selectedId} onSelect={setSelectedId} />
					</GridItem>
					<GridItem h="100%" minW="0">
						{thread}
					</GridItem>
					{showInfo && infoAsColumn && (
						<GridItem h="100%" minW="0" borderLeftWidth="1px" borderColor="wa.divider">
							<ContactInfoPanel conversationId={selectedId} onClose={closeInfo} variant="column" />
						</GridItem>
					)}
				</>
			)}

			{/* Below `xl` the panel is a drawer; it is rendered here (not in the
			    column branch) so the same instance serves both layouts. */}
			{showInfo && !infoAsColumn && (
				<ContactInfoPanel conversationId={selectedId} onClose={closeInfo} variant="drawer" />
			)}
		</Grid>
	);
}
